import { authFetch } from "@/lib/api-client";
import type { EventsChartLatencyMetric } from "@/features/admin/lib/events-chart-metric";

// ─── Operations settings ────────────────────────────────────────────
// How the Events, Scheduled jobs, and Token usage tabs behave: retention,
// defaults, stuck thresholds, failure and spend alerts, the monthly LLM
// budget. One record for the whole platform, same singleton shape as the
// changelog settings.

/** What the usage chart stacks by. Mirrors `TOKEN_USAGE_GROUPINGS` in @nexus/db. */
export type TokenUsageGrouping = "model" | "feature" | "tenant";

export interface AdminOpsSettings {
  /** Days of event history to keep. Zero keeps everything. */
  eventsRetentionDays: number;
  /** Whether the Events tab opens with health pings hidden. */
  eventsHidePingsDefault: boolean;
  /** The time window the Events tab opens scoped to, in hours. */
  eventsDefaultWindowHours: number;
  /** Minutes a run may sit in PROCESSING before the tab shows it as stuck. */
  eventsStuckAfterMinutes: number;
  /** Posts to Slack when failures pile up. */
  alertOnFailedEvents: boolean;
  /** Failed runs in the last hour it takes to raise that alert. */
  failedEventsAlertThreshold: number;
  /** Posts to Slack when a user action goes over its end-to-end budget. */
  alertOnSlowActions: boolean;
  /** The budget an action gets when its event type has no override. */
  slowActionSecondsDefault: number;
  /** Per-event-type budget overrides, in seconds. */
  slowActionSecondsByEventType: Record<string, number>;
  /** Which latency the Activity chart plots: the wait, the work, or both. */
  eventsChartLatencyMetric: EventsChartLatencyMetric;
  /** Whether the default budget is drawn across the Activity chart. */
  eventsChartShowBudgetLine: boolean;
  /** Minutes of slack before an enabled job past its next run is flagged overdue. */
  jobsOverdueGraceMinutes: number;
  /** Consecutive failures before a job's badge turns red rather than amber. */
  jobsUnhealthyAfterFailures: number;
  /** Seconds between automatic refreshes of the Scheduled jobs tab. Zero is manual. */
  jobsAutoRefreshSeconds: number;
  /** Days of LLM usage history to keep. Zero keeps everything. */
  tokenUsageRetentionDays: number;
  /** The time window the Token usage tab opens scoped to, in hours. */
  tokenUsageDefaultWindowHours: number;
  /** Seconds between automatic refreshes of the Token usage tab. Zero is manual. */
  tokenUsageAutoRefreshSeconds: number;
  /** What the usage chart stacks by when the tab opens. */
  tokenUsageDefaultGrouping: TokenUsageGrouping;
  /** The share of the window a row needs to survive the "Hide under N%" filter. */
  tokenUsageLongTailPercent: number;
  /** Posts to Slack when the window's token spend crosses the threshold. */
  alertOnTokenSpend: boolean;
  /** Total tokens in the window it takes to raise that alert. */
  tokenSpendAlertThreshold: number;
  /** Hours the spend alert totals over: 1, 6 or 24. */
  tokenSpendAlertWindowHours: number;
  /** Hours of silence after any token alert fires. */
  tokenSpendAlertCooldownHours: number;
  /** Posts to Slack when a single tenant crosses its own threshold. */
  alertOnTenantTokenSpend: boolean;
  /** Tokens one tenant may spend in the window before that alert is raised. */
  tenantTokenSpendAlertThreshold: number;
  /** Posts to Slack when p95 latency degrades. */
  alertOnLlmLatency: boolean;
  /** The p95 over the window, in milliseconds, that raises that alert. */
  llmLatencyAlertP95Ms: number;
  /** Posts to Slack when the share of failed calls crosses the percent below. */
  alertOnLlmFailureRate: boolean;
  /** Failed calls as a percent of all calls. Also the Fail % mark on the tab. */
  llmFailureRateAlertPercent: number;
  /** Month-to-date alert cap in US cents. Zero means no budget is set. */
  monthlyBudgetUsdCents: number;
  /** Percent of budget that raises the first warning; 100% always alerts too. */
  budgetAlertAtPercent: number;
  /**
   * What a TB of scanned bytes costs, in micro-dollars. Zero means no rate is
   * set and every BigQuery cost reads as unavailable rather than as zero.
   */
  bigQueryPricePerTbMicros: number;
  /** Days of BigQuery usage history to keep. No keep-forever position here. */
  bigQueryRetentionDays: number;
  /** Posts to Slack when bytes billed across the window cross the threshold. */
  alertOnBigQueryBytes: boolean;
  /** Bytes billed in the window it takes to raise that alert. */
  bigQueryBytesAlertThreshold: number;
  /** Posts to Slack when a single query scans past its own threshold. */
  alertOnBigQuerySingleQueryBytes: boolean;
  /** Bytes one query may scan before that alert is raised. */
  bigQuerySingleQueryBytesThreshold: number;
  /** Hours both BigQuery alerts read over: 1, 6 or 24. */
  bigQueryBytesAlertWindowHours: number;
  /** Hours of silence after a BigQuery alert fires. */
  bigQueryBytesAlertCooldownHours: number;
  /** Posts to Slack when a scheduled job's failure streak turns the badge red. */
  alertOnJobFailures: boolean;
  /** Posts to Slack when an enabled job stays overdue past the grace window. */
  alertOnJobMissed: boolean;
  /** Posts to Slack on create / pause / resume / delete / Run now. */
  alertOnJobLifecycle: boolean;
  /** Null until somebody has saved — the page is showing defaults. */
  updatedByName: string | null;
  updatedAt: string | null;
  /** False when no webhook is set, so the page can warn before alerts are switched on. */
  slackConfigured: boolean;
}

/** The fields a save may change. The credit and the Slack flag are read-only. */
export type AdminOpsSettingsInput = Omit<
  AdminOpsSettings,
  "updatedByName" | "updatedAt" | "slackConfigured"
>;

export async function fetchAdminOpsSettings(): Promise<AdminOpsSettings> {
  const { data } = await authFetch<AdminOpsSettings>("/admin/settings/ops");
  return data;
}

/** Partial: the page sends the fields that changed, not the whole record. */
export async function updateAdminOpsSettings(
  input: Partial<AdminOpsSettingsInput>,
): Promise<AdminOpsSettings> {
  const { data } = await authFetch<AdminOpsSettings>("/admin/settings/ops", {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}
